/**
 * Admin user-management API. Targets admin-api (`:8010`) which exposes
 * search, dossier, ban, unban, and the manual credit-grant route.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackAdminEvent } from '../lib/posthog.js';
import { ADMIN_API_BASE, apiFetch } from './client.js';

// ─── Search ──────────────────────────────────────────────────────────

export interface UserSearchResult {
  id: string;
  email: string;
  phoneE164: string | null;
  status: string;
  createdAt: string;
}

export const userKeys = {
  all: ['admin-users'] as const,
  search: (q: string) => [...userKeys.all, 'search', q] as const,
  detail: (userId: string) => [...userKeys.all, 'detail', userId] as const,
  audit: (userId: string) => [...userKeys.all, 'audit', userId] as const,
  profileFull: (userId: string) => [...userKeys.all, 'profile-full', userId] as const,
} as const;

export function useUserSearchQuery(q: string) {
  return useQuery({
    queryKey: userKeys.search(q),
    enabled: q.trim().length > 0,
    queryFn: () =>
      apiFetch<{ results: UserSearchResult[] }>({
        method: 'GET',
        url: `/admin/users/search?q=${encodeURIComponent(q.trim())}`,
        baseUrl: ADMIN_API_BASE,
      }),
  });
}

// ─── Detail (dossier) ────────────────────────────────────────────────

export interface UserDetail {
  user: {
    id: string;
    email: string;
    phoneE164: string | null;
    status: string;
    banReason: string | null;
    bannedAt: string | null;
    createdAt: string;
    riskScore: number;
    lastSeenAt: string | null;
    riskSignalsCount: number;
    isFounderGrant: boolean;
    demoReviewer: boolean;
    signupTrialPerksUntil: string | null;
    openSupportTickets: number;
    pendingBanAppeals: number;
  };
  profile: {
    displayName: string | null;
    gender: string | null;
    dateOfBirth: string | null;
    countryOfResidenceIso: string | null;
    maritalStatus: string | null;
  } | null;
  creditBalance: number;
  recentLedger: Array<{
    id: string;
    delta: number;
    reason: string;
    createdAt: string;
  }>;
  recentReports: Array<{
    id: string;
    reason: string;
    status: string;
    createdAt: string;
    direction: 'filed_by' | 'filed_against';
  }>;
  activeBan: {
    kind: string;
    reason: string;
    bannedAt: string;
    issuedByAdminId: string | null;
    /** Report rows that triggered an auto-ban (null for manual bans
     *  or auto-bans issued before the column existed). Each entry is
     *  pre-joined server-side so the UI renders the trail in one paint
     *  without a follow-up round-trip. Plan §Trust & Safety §Auto-ban
     *  audit-trail enrichment. */
    triggeringReports: Array<{
      id: string;
      reason: string;
      status: string;
      createdAt: string;
      reporterUserId: string;
      reportedUserId: string;
    }> | null;
  } | null;
}

export function useUserDetailQuery(userId: string | null) {
  return useQuery({
    queryKey: userKeys.detail(userId ?? ''),
    enabled: !!userId,
    queryFn: () =>
      apiFetch<UserDetail>({
        method: 'GET',
        url: `/admin/users/${encodeURIComponent(userId ?? '')}`,
        baseUrl: ADMIN_API_BASE,
      }),
  });
}

// ─── Audit log ──────────────────────────────────────────────────────

export interface AuditLogEntry {
  id: string;
  actorKind: string;
  actorId: string | null;
  actorEmail: string | null;
  action: string;
  payload: unknown;
  createdAt: string;
}

export function useUserAuditLogQuery(userId: string | null) {
  return useQuery({
    queryKey: userKeys.audit(userId ?? ''),
    enabled: !!userId,
    queryFn: () =>
      apiFetch<{ entries: AuditLogEntry[] }>({
        method: 'GET',
        url: `/admin/users/${encodeURIComponent(userId ?? '')}/audit`,
        baseUrl: ADMIN_API_BASE,
      }),
  });
}

// ─── Full profile ───────────────────────────────────────────────────

export interface ProfilePhoto {
  id: string;
  position: number;
  s3KeyOriginal: string;
  s3KeyBlurred: string | null;
  s3KeyThumbClear: string | null;
  /** Short-lived signed GET URL to the clear original. Null when
   *  admin-api booted without S3 env vars (e.g. local dev without
   *  LocalStack). */
  clearUrl: string | null;
  thumbUrl: string | null;
  moderationStatus: string;
  moderationReason: string | null;
  widthPx: number;
  heightPx: number;
  createdAt: string;
}

export interface ProfileFull {
  profile: Record<string, unknown> | null;
  photos: ProfilePhoto[];
}

export function useUserProfileFullQuery(userId: string | null) {
  return useQuery({
    queryKey: userKeys.profileFull(userId ?? ''),
    enabled: !!userId,
    queryFn: () =>
      apiFetch<ProfileFull>({
        method: 'GET',
        url: `/admin/users/${encodeURIComponent(userId ?? '')}/profile-full`,
        baseUrl: ADMIN_API_BASE,
      }),
  });
}

// ─── Ban / Unban ─────────────────────────────────────────────────────

export function useBanUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
      apiFetch<{ banned: true }>({
        method: 'POST',
        url: `/admin/users/${encodeURIComponent(userId)}/ban`,
        body: { reason },
        baseUrl: ADMIN_API_BASE,
      }),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: userKeys.detail(userId) });
      qc.invalidateQueries({ queryKey: userKeys.all });
      // Audit_log is the load-bearing record; PostHog event is
      // overlay-only and lets us track admin throughput patterns.
      // No reason text in the event — that's PII-adjacent (the
      // audit row already has it server-side).
      trackAdminEvent('admin.user.banned', { targetUserId: userId });
    },
  });
}

export function useUnbanUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
      apiFetch<{ unbanned: true }>({
        method: 'POST',
        url: `/admin/users/${encodeURIComponent(userId)}/unban`,
        body: { reason },
        baseUrl: ADMIN_API_BASE,
      }),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: userKeys.detail(userId) });
      qc.invalidateQueries({ queryKey: userKeys.all });
      trackAdminEvent('admin.user.unbanned', { targetUserId: userId });
    },
  });
}

// ─── Credit grant ────────────────────────────────────────────────────

export function useGrantCreditsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      amount,
      reason,
    }: {
      userId: string;
      amount: number;
      reason: string;
    }) =>
      apiFetch<{ ok: true; ledgerId: string; newBalance: number }>({
        method: 'POST',
        url: '/admin/credits/grant',
        body: { userId, amount, reason },
        baseUrl: ADMIN_API_BASE,
      }),
    onSuccess: (data, { userId, amount }) => {
      qc.invalidateQueries({ queryKey: userKeys.detail(userId) });
      // Direction is more useful than magnitude for funnel — the
      // exact amount + reason are already in the audit row.
      trackAdminEvent('admin.credits.adjusted', {
        targetUserId: userId,
        direction: amount > 0 ? 'grant' : 'revoke',
        newBalance: data.newBalance,
      });
    },
  });
}
